/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Button, ModalConfirm } from "@nordcloud/gnui";
import { showError, showSuccess } from "~/services/toast";
import { inflect, isNotEmpty } from "~/tools";
import { useDeleteRoleBinding } from "~/views/permissions/hooks/useDeleteRoleBinding/useDeleteRoleBinding";
import { useDeleteApiKey } from "../hooks/useDeleteApiKey/useDeleteApiKey";

type Props = {
  apiKeyIds: string[];
  rolesIds: string[];
  isDeleteModalOpen: boolean;
  openModal: () => void;
  close: () => void;
  onSuccess?: () => void;
};

export function DeleteApiKeys({
  apiKeyIds,
  rolesIds,
  isDeleteModalOpen,
  openModal,
  close,
  onSuccess,
}: Props) {
  const [deleteApiKey] = useDeleteApiKey({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
  });

  const [deleteRoleBinding] = useDeleteRoleBinding({
    onSuccess: () => null,
  });

  async function deleteApiKeys(ids: string[]) {
    if (isNotEmpty(rolesIds)) {
      await Promise.all(rolesIds.map((id) => deleteRoleBinding(id)));
    }

    if (ids.length === 1) {
      return deleteApiKey(ids[0])
        .then(() => showSuccess(`API Key has been removed.`))
        .catch(() => showError("Something went wrong."));
    }

    return Promise.all(ids.map((id) => deleteApiKey(id)))
      .then(() => showSuccess(`${ids.length} API Keys has been removed.`))
      .catch(() => showError("Something went wrong."));
  }

  async function onDelete(ids: string[]) {
    await deleteApiKeys(ids);
  }

  return (
    <>
      <Button
        aria-label="delete button"
        icon="trash"
        size="md"
        severity="low"
        onClick={openModal}
      />
      <ModalConfirm
        onClose={close}
        isOpen={isDeleteModalOpen}
        confirm={() => onDelete(apiKeyIds)}
        actionLabel="Delete"
        data-testid="modal-api-key-delete"
        contentLabel={`Delete ${inflect("API Key")(apiKeyIds.length)}`}
      >
        {`Are you sure you want to delete ${inflect("API Key")(
          apiKeyIds.length
        )}?`}
      </ModalConfirm>
    </>
  );
}
